import React, { useState } from 'react';
import { userReviewAPI } from '../../services/api';
import { getStorageUrl } from '../../config/api';
import './ReviewCard.css';

const ReviewCard = ({ review, currentUserId, onUpdated, onDeleted }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const isMyReview = (review.reviewer_id || review.reviewer?.id) === currentUserId;

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      setIsDeleting(true);
      const response = await userReviewAPI.deleteReview(review.id);
      
      if (response.success) {
        onDeleted();
      } else {
        alert(response.message || 'Failed to delete review');
      }
    } catch (err) {
      console.error('Error deleting review:', err);
      alert('Failed to delete review. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowConfirmDelete(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`star ${i < rating ? 'filled' : ''}`}>
        ★
      </span>
    ));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getRelationshipLabel = (type) => {
    const relationships = {
      'colleague': 'Colleague',
      'manager': 'Manager',
      'report': 'Reported to',
      'client': 'Client',
      'peer': 'Peer',
      'other': 'Other',
    };
    return relationships[type] || 'Other';
  };

  return (
    <div className={`review-card ${review.is_featured ? 'featured' : ''}`}>
      <div className="review-header">
        <div className="reviewer-info">
          {review.reviewer?.profile_photo || review.reviewer?.profile_photo_url ? (
            <img 
              src={review.reviewer.profile_photo_url || 
                   (review.reviewer.profile_photo?.startsWith('http') 
                     ? review.reviewer.profile_photo 
                     : getStorageUrl(review.reviewer.profile_photo))} 
              alt={review.reviewer.name}
              className="reviewer-avatar"
            />
          ) : (
            <div className="reviewer-avatar-placeholder">
              {review.reviewer?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          )}
          <div className="reviewer-details">
            <div className="reviewer-name">{review.reviewer?.name || 'Anonymous'}</div>
            <div className="reviewer-relationship-info">
              {review.relationship_type && (
                <span className="reviewer-relationship">
                  {getRelationshipLabel(review.relationship_type)}
                </span>
              )}
              {review.company && (
                <>
                  {review.relationship_type && <span className="relationship-separator"> • </span>}
                  <span className="reviewer-company">{review.company}</span>
                </>
              )}
            </div>
            <div className="review-date">{formatDate(review.created_at)}</div>
          </div>
        </div>
        
        {isMyReview && (
          <div className="review-actions">
            <button
              className="btn-delete-review"
              onClick={handleDelete}
              disabled={isDeleting}
              title="Delete review"
            >
              {isDeleting ? (
                'Deleting...'
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="review-rating">
        {renderStars(review.rating)}
      </div>

      <div className="review-text">
        {review.review_text}
      </div>

      {review.is_featured && (
        <div className="review-featured-badge">Featured</div>
      )}
    </div>
  );
};

export default ReviewCard;
